import { BehaviorSubject, Observable, Observer, catchError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OktaAuth, IDToken, AccessToken, RefreshToken } from '@okta/okta-auth-js';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie';

@Injectable({ providedIn: 'root' })
export class OktaAuthService {

  private CLIENT_ID = `${environment.oktaClientId}`;
  private ISSUER = `${environment.oktaBaseUrl}${environment.oktaIssuer}`;
  private LOGIN_REDIRECT_URI = `${environment.webApplicationBaseUri}/callback`;
  private LOGIN_URI = `${environment.webApplicationBaseUri}/login`;
  private OKTA_BASE_URI: string =`${environment.oktaBaseUrl}`;
  private PIV_LOGIN_URL: string = `${this.OKTA_BASE_URI}/login/cert?fromURI=${this.LOGIN_URI}`;

  oktaAuth = new OktaAuth({
    clientId: this.CLIENT_ID,
    issuer: this.ISSUER,
    redirectUri: this.LOGIN_REDIRECT_URI,
    scopes: ['openid', 'email', 'profile', 'offline_access'],
    pkce: false
  });

  $userSession: BehaviorSubject<ServerModels.OktaUserSessionResponse> = new BehaviorSubject<ServerModels.OktaUserSessionResponse>(null);

  constructor(protected http: HttpClient, private router: Router) {
  }

  public isFullyAuthenticated(): boolean {
    // Full authentication means we have both an OKTA session and OKTA access token
    return !!this.getOktaSessionFromStorage() && !!this.getCachedOktaAccessTokenFromStorage();
  }

  private async storeOktaAccessToken() {
      this.oktaAuth.tokenManager.get('accessToken').then(token => {
        sessionStorage.setItem('okta-accesstoken', token['accessToken']);
      });
  }

  public storeOktaSession(userSession: ServerModels.OktaUserSessionResponse): any {
    sessionStorage.setItem('okta-session', JSON.stringify(userSession));
  }

  public login(originalUrl: string) {
    // Launches the login redirect.
    this.oktaAuth.token.getWithRedirect();
  }

  public async handleAuthentication() {
    const tokenContainer = await this.oktaAuth.token.parseFromUrl();

    this.oktaAuth.tokenManager.add('idToken', tokenContainer.tokens.idToken as IDToken);
    this.oktaAuth.tokenManager.add('accessToken', tokenContainer.tokens.accessToken as AccessToken);

    this.storeOktaAccessToken();

    // navigate back
    this.router.navigateByUrl(this.LOGIN_URI);
  }

  public clearStorage() {
    sessionStorage.removeItem("okta-session");
    sessionStorage.removeItem("okta-accesstoken");
    sessionStorage.removeItem("okta-app-url");
    localStorage.removeItem("okta-cache-storage");
    localStorage.removeItem("okta-shared-transaction-storage");
    this.oktaAuth.tokenManager.clear();
  }

  public async logout() {
    this.clearStorage();
    await this.oktaAuth.revokeAccessToken();
    await this.oktaAuth.revokeRefreshToken()
    await this.oktaAuth.signOut({
      postLogoutRedirectUri: this.LOGIN_URI
    });
  }

  public getOktaSessionFromStorage(): ServerModels.OktaUserSessionResponse {
    return JSON.parse(sessionStorage.getItem('okta-session'));
  }

  public getCachedOktaAccessTokenFromStorage(): any {
    return sessionStorage.getItem("okta-accesstoken");
  }


}
